import React, { useEffect, useState } from 'react';
import { employeeWalletAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, UtensilsCrossed, Bus, Heart, Shield, Gift, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const fmt = (n) => `₦${Math.round(Number(n || 0)).toLocaleString()}`;
const CATEGORY_META = {
  food: { icon: UtensilsCrossed, color: '#f59e0b', label: 'Food & Meals', desc: 'Via vendors & vouchers' },
  transport: { icon: Bus, color: '#ec4899', label: 'Transport', desc: 'Via company arrangements' },
  health: { icon: Heart, color: '#00B894', label: 'Health Cover', desc: 'HMO provider' },
  insurance: { icon: Shield, color: '#8b5cf6', label: 'Life Insurance', desc: 'Insurance provider' },
};

export default function EmployeeBenefitsPlan() {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try { setBenefits(await employeeWalletAPI.getBenefitBalances()); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const totalBenefits = benefits.reduce((s, b) => s + b.allocated, 0);

  return (
    <div className="space-y-5 pb-6" data-testid="employee-benefits-plan">
      <h1 className="text-xl font-bold text-gray-900">My Benefits Plan</h1>
      <p className="text-sm text-gray-500">Your company provides <strong className="text-gray-900">{fmt(totalBenefits)}/month</strong> in benefits delivered through approved vendors.</p>

      <div className="space-y-3">
        {benefits.map(b => {
          const meta = CATEGORY_META[b.category] || { icon: Gift, color: '#9ca3af', label: b.category, desc: '' };
          const Icon = meta.icon;
          const pct = b.allocated > 0 ? (b.used / b.allocated) * 100 : 0;

          return (
            <Card key={b.category} className="border-gray-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}18` }}>
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{meta.label}</p>
                      <p className="text-[11px] text-gray-400">{fmt(b.allocated)}/month • {meta.desc}</p>
                    </div>
                  </div>
                </div>
                {b.is_auto ? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Status: <strong className="text-emerald-600">Active</strong></span>
                    <span className="text-[11px] text-gray-400">Auto-deducted on 1st of each month</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-gray-500">Used: <strong className="text-gray-900">{fmt(b.used)}</strong></span>
                      <span className="text-xs text-gray-500">Remaining: <strong className="text-gray-900">{fmt(b.remaining)}</strong></span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                    </div>
                    <Button size="sm" variant="outline" className="text-xs h-7 border-gray-200 text-gray-600 hover:text-pink-600 hover:border-pink-200"
                      onClick={() => navigate(`/app/employee/benefits/vendors?category=${b.category}`)}>
                      Browse {meta.label} Vendors <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-blue-100 bg-blue-50">
        <CardContent className="p-4">
          <p className="text-xs text-blue-700">Benefits are provided by your company through approved vendors — not deposited as cash. Changes to your plan require company admin approval.</p>
        </CardContent>
      </Card>
    </div>
  );
}
